import { useTranslation } from 'react-i18next';
import type { TrainerAssociation } from '../../types/trainerAssociation';

interface TrainerCardProps {
  association: TrainerAssociation;
  onEditPricing: () => void;
  onEditPermissions: () => void;
  onRemove: () => void;
}

export function TrainerCard({
  association,
  onEditPricing,
  onEditPermissions,
  onRemove,
}: TrainerCardProps) {
  const { t } = useTranslation();
  const isPending = association.status === 'Pending';
  const isConfirmed = association.status === 'Confirmed' || association.status === 'Accepted';

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-surface-card rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        {association.trainerAvatarUrl ? (
          <img
            src={association.trainerAvatarUrl}
            alt={association.trainerDisplayName}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-surface-app-elevated flex items-center justify-center">
            <svg className="w-6 h-6 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-ink-primary truncate">{association.trainerDisplayName}</p>
          <p className="text-sm text-ink-secondary truncate">{association.trainerEmail}</p>
        </div>
        {/* Status Badge */}
        {isPending ? (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-brand-accent/20 text-brand-accent text-xs">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('trainers.pending', 'Oczekujący')}
          </span>
        ) : isConfirmed ? (
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-brand-confirm/30 text-brand-confirm-deep text-xs">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('trainers.confirmed', 'Potwierdzony')}
          </span>
        ) : null}
      </div>

      {/* Pending: Show request date and info */}
      {isPending && (
        <div className="mt-3 pt-3 border-t border-border-strong">
          <p className="text-xs text-ink-muted">
            {t('trainers.requestSent', 'Wysłano')}: {formatDate(association.requestedAt || association.createdAt)}
          </p>
          <p className="text-xs text-ink-muted italic mt-1">
            {t('trainers.acceptViaEmail', 'Trener musi zaakceptować zaproszenie przez email')}
          </p>
        </div>
      )}

      {/* Confirmed: Show pricing and permissions */}
      {isConfirmed && (
        <>
          {/* Pricing Info */}
          <div className="mt-3 pt-3 border-t border-border-strong flex items-center gap-3">
            <span className="text-sm font-medium text-ink-primary">
              {association.grossHourlyRate != null || association.hourlyRate != null
                ? `${(association.grossHourlyRate || association.hourlyRate)?.toFixed(2)} PLN/h`
                : t('trainers.noPricing', 'Brak cennika')}
            </span>
            {association.vatRate != null && (
              <span className="text-xs text-ink-secondary bg-surface-app-elevated px-2 py-0.5 rounded">
                VAT {association.vatRate}%
              </span>
            )}
          </div>

          {/* Permission Badges */}
          <div className="flex flex-wrap gap-2 mt-2">
            {association.isEmployee && (
              <span className="text-xs px-2 py-1 rounded bg-brand-primary/15 text-brand-primary">
                {t('trainers.employee', 'Pracownik')}
              </span>
            )}
            {association.canRunOwnTrainings && (
              <span className="text-xs px-2 py-1 rounded bg-brand-primary-light/20 text-brand-primary">
                {t('trainers.ownTrainings', 'Własne treningi')}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3 pt-3 border-t border-border-strong">
            <button
              onClick={onEditPricing}
              className="flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('trainers.pricing', 'Cennik')}
            </button>
            <button
              onClick={onEditPermissions}
              className="flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t('trainers.permissions', 'Uprawnienia')}
            </button>
            <button
              onClick={onRemove}
              className="flex items-center justify-center gap-1 py-2 px-3 rounded-lg bg-brand-warm text-white hover:bg-brand-warm-deep transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
